import * as Yup from 'yup';
import { DateTime } from 'luxon';

import PaymentForm from './Components/PaymentForm';

import BalanceInfo from './Components/BalanceInfo';

import OpenInvoices from './Components/OpenInvoices';

import { validateLuhn, validateExpiryDate } from '../../utils/validation';

import { displayLabelCustomer } from '../../utils/displayLabels';

import StatusDot from '../../utils/utilities/StatusDot';

import { methods } from './helpers';

import { BasicFlex } from '../../utils/Common';

import { formatCurrency } from '../../utils/util';

import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import UnpublishedRoundedIcon from '@mui/icons-material/UnpublishedRounded';
import TimelapseRoundedIcon from '@mui/icons-material/TimelapseRounded';

import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';

let methodObj = methods.reduce((acc, method) => ({...acc, [method.value]: method.label}), {});

const headers = [
  {
    label: "Payment Num",
    id: "payment_num"
  },
  {
    label: 'Customer',
    id: 'client_name'
  },
  {
    label: 'Payment Method',
    id: 'method',
    render: row => methodObj[row.method]
  },
  {
    label: 'Amount',
    id: 'display_amount',
    currency: true
  },
  {
    label: 'Status',
    id: 'status',
    multi: true,
    values: [{value: "accepted", status: "success", label: "Accepted"}, {value: "declined", status: "error", label: "Declined"}]
  },
  {
    id: "available_amount",
    label: "Available Amount",
    currency: true
    // render: row => {
    //   let active = row.available_amount > 0 ? "success" : "";
    //   return (
    //     <BasicFlex width="auto" gap=".5rem"><StatusDot color={active} /> <span>{formatCurrency(row.available_amount)}</span></BasicFlex>
    //   )
    // }
  },
  {
    label: "Date",
    id: "date",
    dateShort: true
  }
];

const filters = [
  {
    id: "available_amount",
    type: "balance",
    label: "Available Amount",
  },
  {
    id: "amount",
    type: "number",
    label: "Amount",
  },
  {
    id: "date",
    type: "date",
    label: "Date",
  }
]

const fields = [
 { 
    fieldType: "debounceSearchSolo",
    formFieldType: "select",
    select: [],
    name: "customer_id",
    label: "Select Customer",
    url: "customers",
    fieldName: "customer_id",
    displayField: "full_name",
    formUpdates: [{name: "client_name", field: "full_name"}],
    setDisplayField: "client_name",
    newItemTitle: "Customer",
    displayLabel: displayLabelCustomer,
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "formField",
    formFieldType: "date",
    name: "date",
    label: "Payment Date",
    type: "date",
    size: {xs: 12, lg: 6},
  },
  // {
  //   fieldType: "divider",
  //   header: "Customer Details",
  //   name: "customer_details",
  //   size: {xs: 12, lg: 12},
  // },
  {
    fieldType: "Component",
    Component: <BalanceInfo />,
    name: "balance_info",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "divider",
    header: "Payment Details",
    name: "payment_details",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "Component",
    Component: <PaymentForm />,
    name: "payment",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "divider",
    header: "Open Invoices",
    name: "invoices",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "Component",
    Component: <OpenInvoices />,
    name: "invoices",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "divider",
    header: "Notes",
    name: "notes",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "formField",
    formFieldType: "text",
    name: "notes",
    label: "Notes",
    type: "text",
    multiline: true,
    rows: 4,
    size: {xs: 12, lg: 12},
  },
]

const tabs = [
  {id: "all", label: "All", Icon: AllInclusiveRoundedIcon },
  { id: "cash", label: "Cash", Icon: PaymentsIcon },
  { id: "credit_card", label: "Credit Card", Icon: CreditCardRoundedIcon },
  { id: "check", label: "Check", Icon: ArticleRoundedIcon },
  { id: "quick pay", label: "Quick Pay", Icon: AccountBalanceRoundedIcon },
  { id: "other", label: "Other", Icon: AccountBalanceWalletRoundedIcon },
]


const initialValues = {
  customer_id: '',
  client_name: '',
  date: "",
  method: '',
  info: '',
  amount: "",
  final_balance: 0,
  notes: '',
  card_num: '',
  exp: '',
  cvv: '',
  entry: 'manual',
  use_cc_fee: false,
  cc_rate: 0,
  processed: false
}

const additionalState = {
  client_name: '',
  client_phone: '',
  applied_payments: [],
}

const schema = Yup.object().shape({
  customer_id: Yup.string().required('Customer is required'),
  amount: Yup.number().min(0, 'Amount must be greater than 0').required('Amount is required'),
  method: Yup.string().required('Payment Method is required'),
  entry: Yup.string().required('Payment Entry is required'),
  
  // info: Yup.string().when('entry', {
  //   is: 'manual',
  //   then: (schema) => schema.required('Payment Info is required'),
  //   otherwise: (schema) => schema
  // }),

  card_num: Yup.string().when('entry', {
    is: (val) => val !== 'manual',
    then: (schema) => schema
      .required('Credit Card Number is required')
      .test('len', 'Invalid card length', (val) => {
        // Remove all non-digits before checking length
        const rawDigits = val?.replace(/\D/g, ''); 
        return rawDigits?.length === 15 || rawDigits?.length === 16;
      })
      .test('luhn-check', 'Invalid card number (Check numbers)', (val) => {
        const rawDigits = val?.replace(/\D/g, '');
        return validateLuhn(rawDigits);
      }),
    otherwise: (schema) => schema
  }),

  exp: Yup.string().when('entry', {
    is: (val) => val !== 'manual',
    then: (schema) => schema
      .required('Expiration Date is required')
      .test('valid-expiry', 'Card is expired or month is invalid', (val) => validateExpiryDate(val)),
    otherwise: (schema) => schema
  }),

  cvv: Yup.string().when('entry', {
    is: (val) => val !== 'manual',
    then: (schema) => schema
      .required('CVV is required')
      .test('len', 'Must be 3 or 4 digits', (val) => val?.length === 3 || val?.length === 4),
    otherwise: (schema) => schema
  }),

  processed: Yup.boolean().when('entry', {
    is: (val) => val !== 'manual',
    then: (schema) => schema.oneOf([true], 'Payment must be processed first'),
    otherwise: (schema) => schema
  })
});

const parseData = (data) => {
  const parsed = {...data};
  parsed.date = DateTime.fromISO(data.date);
  if (parsed.customer_id) parsed.customer_id = data.customer_id.toString();
  parsed.entry = "manual";
  return parsed
}

export { headers, filters, fields, schema, initialValues, parseData, additionalState, tabs };